/* S3 configuration rules — the single TypeScript copy of api/internal/model/storage_validate.go.
 * Used by the MockProvider (422 field errors identical to Go) and by the File storage form (inline errors
 * before anything is sent). Keep the messages byte-identical to the Go constants. */

export const S3_ERR = {
  required: "required",
  region: 'must look like me-central-1 (or "auto" for Cloudflare R2)',
  endpoint: "must be an http(s) URL like https://s3.eu-central-1.wasabisys.com",
  prefix: "letters, digits and / - _ . ! * ' ( ) only; no leading slash or '..' (max 200)",
  accessKey: "an AWS access key ID is 16–128 uppercase letters and digits, usually starting with AKIA (or ASIA) — copy the Access key ID, not the secret",
  compatKey: "invalid access key ID — 3–128 letters, digits and + / = _ . - only",
  secret: "invalid secret access key",
  secretNewKey: "required for a new access key",
} as const;

/** Go model.BucketProblem — AWS bucket naming rules ("" = valid). */
export function bucketProblem(b: string): string {
  if (!b) return S3_ERR.required;
  if (b.length < 3 || b.length > 63) return "must be 3–63 characters";
  if (!/^[a-z0-9][a-z0-9.-]{1,61}[a-z0-9]$/.test(b)) return "lowercase letters, digits, dots and hyphens only; must start and end with a letter or digit";
  if (b.includes("..") || b.includes(".-") || b.includes("-.")) return "dots cannot be next to each other or to a hyphen";
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(b)) return "must not look like an IP address";
  if (b.startsWith("xn--") || b.startsWith("sthree-")) return "must not start with xn-- or sthree-";
  if (/(-s3alias|--ol-s3|\.mrap|--x-s3)$/.test(b)) return "uses a reserved suffix";
  return "";
}

/** Go model.EndpointProblem — custom S3-compatible endpoint ("" = valid). */
export function endpointProblem(e: string): string {
  let u: URL;
  try { u = new URL(e); } catch { return S3_ERR.endpoint; }
  if ((u.protocol !== "https:" && u.protocol !== "http:") || !u.hostname || u.username || u.password || (u.pathname !== "" && u.pathname !== "/") || u.search || u.hash || !/^https?:\/\/[^/]+\/?$/.test(e)) {
    return S3_ERR.endpoint;
  }
  const h = u.hostname;
  if (/[ _]/.test(h) || (!/^[\d.]+$/.test(h) && !h.startsWith("[") && !h.includes(".") && h !== "localhost")) return "invalid host";
  return "";
}

/** Go model.RegionProblem: required; AWS style for Amazon S3, any lowercase code with a custom endpoint. */
export function regionProblem(region: string, endpoint: string): string {
  if (!region) return S3_ERR.required;
  if (region.length > 32) return S3_ERR.region;
  if (/^[a-z]{2}(-[a-z]+)+-\d{1,2}$/.test(region)) return "";
  if (endpoint && /^[a-z0-9]+(-[a-z0-9]+)*$/.test(region)) return "";
  return S3_ERR.region;
}

/** Go model.AccessKeyProblem: required; AWS format (AKIA…/ASIA…) for Amazon S3, looser with a custom endpoint. */
export function accessKeyProblem(id: string, endpoint: string): string {
  if (!id) return S3_ERR.required;
  if (!endpoint && !/^[A-Z0-9]{16,128}$/.test(id)) return S3_ERR.accessKey;
  if (endpoint && !/^[A-Za-z0-9+/=_.-]{3,128}$/.test(id)) return S3_ERR.compatKey;
  return "";
}

/** Go model.PrefixProblem — optional folder prefix ("" = valid). */
export function prefixProblem(p: string): string {
  if (p.length > 200 || !/^[A-Za-z0-9!_.*'()/-]*$/.test(p) || p.includes("..") || p.startsWith("/") || p.includes("//")) return S3_ERR.prefix;
  return "";
}

/** Go model.StorageSettings.ValidateS3 — bucket, region, access key ID and secret are all required.
 *  `secret` is the effective secret (typed, or the stored one when it is kept). */
export function validateS3(st: { bucket: string; region: string; endpoint: string; prefix: string; accessKeyId: string }, secret: string): Record<string, string> {
  const fe: Record<string, string> = {};
  const bp = bucketProblem(st.bucket); if (bp) fe.bucket = bp;
  const rp = regionProblem(st.region, st.endpoint); if (rp) fe.region = rp;
  if (st.endpoint) { const ep = endpointProblem(st.endpoint); if (ep) fe.endpoint = ep; }
  const pp = prefixProblem(st.prefix); if (pp) fe.prefix = pp;
  const kp = accessKeyProblem(st.accessKeyId, st.endpoint); if (kp) fe.accessKeyId = kp;
  if (!secret) fe.secretAccessKey = S3_ERR.required;
  else if (secret.length < 8 || secret.length > 256 || /\s/.test(secret)) fe.secretAccessKey = S3_ERR.secret;
  return fe;
}
